import { useEffect, useState } from 'react';
import { ManageAccounts, Cancel, ContactPage, Delete, Done, Clear } from '@mui/icons-material';

// material-ui
import {
    Box,
    Button,
    Grid,
    Stack,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Modal,
    TextField,
    FormControl,
    Select,
    MenuItem,
    ToggleButtonGroup,
    ToggleButton,
    Checkbox,
    Snackbar,
    Alert,
    CircularProgress
} from '@mui/material';

import { styled, useTheme } from '@mui/material/styles';
import { deepPurple, grey } from '@mui/material/colors';

import { gridSpacing } from 'app/constant';

import { useSelector } from 'react-redux';
import { selectCurrentUser, selectCurrentToken } from 'features/auth/authSlice';
import { Link } from 'react-router-dom';
import { axiosInstance } from 'utils/axios';

import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import useFetchData from 'hooks/useFetchData';
import DailyCheckInTab from './components/DailyCheckInTab';

import { useTranslation } from "react-i18next";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    '& .MuiToggleButtonGroup-grouped': {
        //   margin: theme.spacing(2),

        //   '&.Mui-disabled': {
        //     border: 0,
        //   },
        '&:not(:first-of-type)': {
            borderRadius: theme.shape.borderRadius
        },
        '&:first-of-type': {
            borderRadius: theme.shape.borderRadius
        }
    }
}));

const StyledToggleButton = styled(ToggleButton)({
    border: 2,
    borderColor: deepPurple[500],
    backgroundColor: grey[300],
    '&.Mui-selected, &.Mui-selected:hover': {
        color: 'white',
        backgroundColor: deepPurple[500]
    }
});

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 1000,
    width: '90%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    padding: '20px',
    borderRadius: '10px'
};

const thirdPartyColumns = [
    { id: 'id', label: 'Count', minWidth: 30 },
    { id: 'name', label: 'Third Party Name', minWidth: 170 },
    { id: 'charges', label: 'Charges', minWidth: 170 },
    { id: 'chargesTypeName', label: 'Charges Type Name', minWidth: 170 },
    { id: 'active', label: 'Active', minWidth: 80 },
    { id: 'bankBalance', label: 'Bank Balance', minWidth: 170 },
    { id: 'bankLatestUpdatedDate', label: 'Balance Updated Date', minWidth: 170, type: 'date' },
    { id: 'bankVendorName', label: 'Bank', minWidth: 170 },
    { id: 'bankAccountHolderName', label: 'Bank Account Holder Name', minWidth: 170 },
    { id: 'bankAccountNumber', label: 'Bank Account Number', minWidth: 170 },
    { id: 'iBanNumber', label: 'iBan Number', minWidth: 170 },
    { id: 'isShowFrontend', label: 'Is Show Frontend', minWidth: 80 }
];

const customerGroupColumns = [
    { id: 'id', label: 'Count', minWidth: 30 },
    { id: 'name', label: 'Customer Group Name', minWidth: 170 },
    {
        id: 'active',
        label: 'Active',
        minWidth: 80
    }
];

const bankVendorColumns = [
    { id: 'id', label: 'Count', minWidth: 30 },
    { id: 'name', label: 'Bank Vendor Name', minWidth: 170 },
    {
        id: 'active',
        label: 'Active',
        minWidth: 80
    },
    { id: 'sequenceNumber', label: 'Sequence Number', minWidth: 170 },
    { id: 'isWallet', label: 'Is Wallet', minWidth: 170, type: 'boolean' }
];

const expensesTypeColumns = [
    { id: 'id', label: 'Count', minWidth: 30 },
    { id: 'name', label: 'Bank Vendor Name', minWidth: 170 },
    {
        id: 'active',
        label: 'Active',
        minWidth: 80
    }
];
const inquiryColumns = [
    { id: 'id', label: 'Count', minWidth: 30 },
    { id: 'name', label: 'Bank Vendor Name', minWidth: 170 },
    {
        id: 'active',
        label: 'Active',
        minWidth: 80
    }
];

const nonPaymentDateColumns = [
    { id: 'id', label: 'ID', minWidth: 30 },
    { id: 'dateLabel', label: 'Date Label', minWidth: 170 },
    {
        id: 'createdBy',
        label: 'Created By',
        minWidth: 170
    },
    {
        id: 'updatedBy',
        label: 'Updated By',
        minWidth: 170
    },
    {
        id: 'createdDate',
        label: 'Created Date',
        type: 'date',
        minWidth: 170
    },
    {
        id: 'updatedDate',
        label: 'Updated Date',
        type: 'date',
        minWidth: 170
    }
];

const transactionLimitColumns = [
    { id: 'id', label: 'ID', minWidth: 30 },
    {
        id: 'thirdPartyName',
        label: 'Third Party Name',
        minWidth: 170
    },
    {
        id: 'depositLimit',
        label: 'Deposit Limit',
        minWidth: 120
    },
    {
        id: 'withdrawalLimit',
        label: 'Withdrawal Limit',
        minWidth: 120
    }
];

const payoutRateColumns = [
    { id: 'id', label: 'ID', minWidth: 30 },
    // { id: 'packageID', label: 'Package ID', minWidth: 60 },
    { id: 'packageName', label: 'Package Name', minWidth: 60 },
    {
        id: 'dateLabel',
        label: 'Date',
        type: 'date',
        minWidth: 170
    },
    {
        id: 'rate',
        label: 'Rate',
        minWidth: 60
    }
];

function createThirdPartyData(count, thirdpartyname, charges, chargestypename, active) {
    return { count, thirdpartyname, charges, chargestypename, active };
}

function createExpensesTypeData(count, banvendorname, active) {
    return { count, banvendorname, active };
}
function createInquiryData(id, name, active) {
    return { id, name, active };
}

const thirdPartyRows = [
    createThirdPartyData('1', 'third party name', 'charges', 'charges type name', 'active'),
    createThirdPartyData('1', 'third party name', 'charges', 'charges type name', 'active'),
    createThirdPartyData('1', 'third party name', 'charges', 'charges type name', 'active'),
    createThirdPartyData('1', 'third party name', 'charges', 'charges type name', 'active'),
    createThirdPartyData('1', 'third party name', 'charges', 'charges type name', 'active'),
    createThirdPartyData('1', 'third party name', 'charges', 'charges type name', 'active'),
    createThirdPartyData('1', 'third party name', 'charges', 'charges type name', 'active'),
    createThirdPartyData('1', 'third party name', 'charges', 'charges type name', 'active'),
    createThirdPartyData('1', 'third party name', 'charges', 'charges type name', 'active')
];

const expensesTypeRows = [createExpensesTypeData('1', 'Bank Vendor name', 'active')];
const inquiryRows = [
    createInquiryData('1', 'inquiry name', 'active'),
    createInquiryData('1', 'inquiry name', 'active'),
    createInquiryData('1', 'inquiry name', 'active'),
    createInquiryData('1', 'inquiry name', 'active')
];

const renderTableCell = (value) => {
    if (value === 'active') {
        return <span style={{ padding: '10px', backgroundColor: 'green', color: 'white', borderRadius: '5px' }}>{value}</span>;
    }
    if (value === 'inactive') {
        return <span style={{ padding: '10px', backgroundColor: 'red', color: 'white', borderRadius: '5px' }}>{value}</span>;
    }
    return value;
};

const MasterDataManagement = () => {
    const { t } = useTranslation();

    const user = useSelector(selectCurrentUser);
    const token = useSelector(selectCurrentToken);
    const theme = useTheme();
    const [isLoading, setLoading] = useState(true);
    const [view, setView] = useState('daily-checkin');
    
    const handleChange = (event, nextView) => {
        setView(nextView);
    };

    return (
        <Grid container item xs={12}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%'
                }}
            >
                <StyledToggleButtonGroup
                    value={view}
                    exclusive
                    onChange={handleChange}
                    sx={{
                        flexDirection: { xs: 'column', md: 'row' }
                    }}
                >
                    <StyledToggleButton value="daily-checkin" aria-label="third-party-record">
                        {t("masterData.dailyCheckIn")}
                    </StyledToggleButton>
                </StyledToggleButtonGroup>
            </Box>

            {view === 'daily-checkin' && (
                <>
                    <DailyCheckInTab />
                </>
            )}
        </Grid>
    );
};

export default MasterDataManagement;
